import { useSyncExternalStore } from 'react';

export type Density = 'comfortable' | 'default' | 'compact';

const STORAGE_KEY = 'lognai-density';
const VALID: readonly Density[] = ['comfortable', 'default', 'compact'];

export const DENSITY_LABELS: Record<Density, string> = {
  comfortable: 'Comfortable',
  default: 'Default',
  compact: 'Compact',
};

export const DENSITY_HINTS: Record<Density, string> = {
  comfortable: 'More breathing room around each row.',
  default: 'Balanced for everyday work.',
  compact: 'Most rows on screen — for long lists and smaller displays.',
};

function getStored(): Density {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Density | null;
    return stored && VALID.includes(stored) ? stored : 'default';
  } catch {
    return 'default';
  }
}

// Module-level singleton — shared across all hook instances in the same tab.
let current: Density = getStored();
const listeners = new Set<() => void>();

/**
 * Density is a single attribute on <html>; the design system's `--space-*`
 * custom properties key off it, so nothing needs to re-render for the whole
 * app to retune.
 */
function apply(mode: Density) {
  document.documentElement.setAttribute('data-density', mode);
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {}
}

// Apply on module load so the attribute is set before first paint.
apply(current);

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return current;
}

export function setDensity(next: Density) {
  current = next;
  apply(next);
  listeners.forEach((l) => l());
}

export function useDensity() {
  const density = useSyncExternalStore(subscribe, getSnapshot);
  return { density, setDensity };
}
